import {
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {Router} from "@angular/router";
import {Subscription} from "rxjs";

import {isRegion2, REGION, REGION2} from '../../defines/gukeo.const';
import {
  REGION_LIST,
  GetRegion2Css, GetRegion2CssPos, GetRegion2Name, RESULT_LINKPOPUP, DEFAULT_SCROLL_SPEED
} from '../../defines/client.const';
import {SimpleDialect} from '../../models/dialect.model';
import {SimpleWord} from '../../models/simple-word.model';
import {multiLinkMixin} from "../../mixins/multi-link.mixin";
import {DynamicComponentService} from "../../services/dynamic-component/dynamic-component.service";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {LayoutMgrService} from "../../services/layout-mgr/layout-mgr.service";
import {ScrollDownService} from "../../services/scroll-down/scroll-down.service";
import {
  BubbleLayoutBox,
  BubblePlacement,
  resolveBubblePlacements,
} from './dialect-map-bubble-layout';


class RegionBox {
  region2: REGION2;
  names: Array<string>;
  ids: Array<string>; // 지방어 아이디 목록

  constructor(region2: REGION2, name: string, id: string) {
    this.region2 = region2;
    this.names = [name];
    this.ids = [id];
  }

  public get area(): string {
    return GetRegion2Name(this.region2);
  }

  public add(name: string, id: string): void {
    this.names.push(name);
    this.ids.push(id);
  }

  public get names_txt(): string {
    return this.names.join(', ');
  }

  public get ids_txt(): string {
    return this.ids.join(',');
  }
}

type BubbleLayoutElement = BubbleLayoutBox & {
  el: HTMLElement;
};

@Component({
  selector: 'app-dialect-map',
  templateUrl: './dialect-map.component.html',
  styleUrls: ['./dialect-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialectMapComponent extends multiLinkMixin implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('style.display') display = 'block';
  @ViewChild('container') container: ElementRef;
  @ViewChild('mapBox') mapBox: ElementRef<HTMLElement>;
  @ViewChildren('regionBubble') regionBubbles: QueryList<ElementRef<HTMLElement>>;
  @Output() closePopup: EventEmitter<any>;
  @Input() word: SimpleWord;
  @Input() set dialects(values: Array<SimpleDialect>) {
    const mapRegionBox = new Map<REGION2, RegionBox>();
    values.forEach(dialect => {
      if (dialect.area1 && isRegion2(dialect.area1)) {
        const regionBox = mapRegionBox.get(dialect.area1);
        if (regionBox) {
          regionBox.add(dialect.word, dialect.id);
        }
        else {
          mapRegionBox.set(dialect.area1, new RegionBox(dialect.area1, dialect.word, dialect.id));
        }
      }
    });

    this.listRegionBox = Array.from(mapRegionBox.values());
    // 데이터가 실제로 바뀐 경우에만 재시도 카운터/포기 플래그를 리셋한다.
    // 부모가 매 변경 감지마다 새 배열 참조를 흘려도(흔한 OnPush 외 패턴), 동일한 dialect id 집합이면
    // abandon 가드를 풀지 않아 mapBox 0 환경에서 경고 폭주가 다시 살아나지 않는다.
    // SimpleDialect.id는 모델 정의상 null일 수 있고 ','는 id에 포함될 가능성도 있으므로
    // 구분자로 unit separator(\x1f) + null 위치를 인덱스로 식별해 시그니처 충돌을 막는다.
    const nextSig = values.map((dialect, i) => dialect.id ?? `<no-id:${i}>`).join('\x1f');
    if (this.dialectsSig !== nextSig) {
      this.dialectsSig = nextSig;
      this.retryCount = 0;
      this.layoutAbandoned = false;
      this.abandonedTriggerLogged = false;
    }
    this.scheduleBubbleLayout();
  }

  protected readonly GetRegion2CssPos = GetRegion2CssPos;
  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2Name = GetRegion2Name;

  public listRegion = REGION_LIST.filter(r => r.region !== REGION.ALL);

  public listRegionBox?: Array<RegionBox>;
  public mapBottomPadding = 0;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;
  private regionBubbles$: Subscription | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private layoutFrame: number | null = null;
  private retryHandle: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  // MAX 도달 후 후속 외부 트리거(ResizeObserver 등)가 들어와도 동일 사이클을 반복하지 않도록 차단한다.
  // dialects 입력이 새로 들어오면(데이터가 갱신되면) 다시 시도할 기회를 부여한다.
  private layoutAbandoned = false;
  // abandon 상태에서 첫 외부 트리거를 한 번 로그로 남겨 "왜 더 이상 배치가 안 되는지" 진단 가능하게 한다.
  // 두 번째부터는 침묵하므로 경고 폭주는 발생하지 않는다.
  private abandonedTriggerLogged = false;
  // ViewChild/ViewChildren 미바인딩 경고를 한 번만 출력하도록 가드한다.
  // 두 호출 사이트가 서로 다른 필드 조합을 검사하므로 플래그를 분리해 한 쪽 발화가 다른 쪽을 침묵시키지 않게 한다.
  // - warnedRegionBubblesMissingInAfterViewInit: ngAfterViewInit에서 regionBubbles만 검사
  // - warnedViewBindingMissingInUpdate: updateBubbleLayout에서 mapBox + regionBubbles 둘 다 검사
  private warnedRegionBubblesMissingInAfterViewInit = false;
  private warnedViewBindingMissingInUpdate = false;
  // dialects 입력의 id 시그니처. 동일 시그니처 재투입 시 abandon 가드를 풀지 않기 위해 사용한다.
  private dialectsSig: string | null = null;
  private static readonly MAX_LAYOUT_RETRIES = 10;
  private static readonly LAYOUT_RETRY_DELAY_MS = 50;


  constructor(
    public router: Router,
    public dynamicSvc: DynamicComponentService,
    public lm: LayoutMgrService,
    private cdr: ChangeDetectorRef,
    private scrollDownSvc: ScrollDownService,
    private globalEventSvc: GlobalEventService,
  ) {
    super(router, dynamicSvc, lm);

    this.closePopup = new EventEmitter<any>();
  }

  ngOnInit(): void {
    console.log('DialectMapComponent::ngOnInit()', this.word, this.dialects);

    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.regionBubbles) {
      this.regionBubbles$ = this.regionBubbles.changes.subscribe(() => this.scheduleBubbleLayout());
    }
    else if (!this.warnedRegionBubblesMissingInAfterViewInit) {
      this.warnedRegionBubblesMissingInAfterViewInit = true;
      console.warn(
        'DialectMapComponent: ngAfterViewInit 시점에 regionBubbles가 미바인딩 상태',
        {wordId: this.word?.id},
      );
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleBubbleLayout());
      if (this.container?.nativeElement) {
        this.resizeObserver.observe(this.container.nativeElement);
      }
      if (this.mapBox?.nativeElement) {
        this.resizeObserver.observe(this.mapBox.nativeElement);
      }
    }

    this.scheduleBubbleLayout();
  }

  ngOnDestroy() {
    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
    if (this.regionBubbles$) {
      this.regionBubbles$.unsubscribe();
      this.regionBubbles$ = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.layoutFrame !== null) {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = null;
    }
    if (this.retryHandle !== null) {
      clearTimeout(this.retryHandle);
      this.retryHandle = null;
    }
  }

  public onClosePopup(): void {
    this.closePopup.emit();
  }

  public onOpenLinkWord($event: MouseEvent, idWord: string): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.OpenMultiLink(idWord).subscribe(resultLinkPopup => {
      console.warn('DialectMapComponent.onOpenLinkWord() resultLinkPopup =', resultLinkPopup);
    });
  }

  public onOpenLinkDialect($event: MouseEvent, idDialect: string): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    const ids = ';' + idDialect;
    this.OpenMultiLink(ids).subscribe(resultLinkPopup => {
      console.warn('DialectMapComponent.onOpenLinkDialect() resultLinkPopup =', resultLinkPopup);

      // TODO: 나중에 여기가 아니라, 라우터 초기화 핸들러에서 터리해야 한다? (고민해볼 것)
      if (resultLinkPopup === RESULT_LINKPOPUP.GOTO_LINK) {
        this.onClosePopup();
      }
    });
  }

  public scheduleBubbleLayout(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.layoutAbandoned) {
      // MAX 한도 초과 후 더 이상 재시도하지 않는다. dialects 입력 갱신 시에만 해제된다.
      // abandon 상태가 외부 트리거를 흡수하고 있다는 사실을 진단 가능하도록 첫 트리거만 로그.
      if (!this.abandonedTriggerLogged) {
        this.abandonedTriggerLogged = true;
        console.warn(
          'DialectMapComponent: abandon 상태에서 외부 트리거 흡수됨(이후 동일 트리거는 침묵)',
          {
            wordId: this.word?.id,
            regionBoxCount: this.listRegionBox?.length ?? 0,
            hasMapBox: !!this.mapBox?.nativeElement,
          },
        );
      }
      return;
    }
    if (this.retryHandle !== null) {
      clearTimeout(this.retryHandle);
      this.retryHandle = null;
    }
    if (this.layoutFrame !== null) {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = null;
    }

    // retryCount는 의도적으로 여기서 리셋하지 않는다.
    // ResizeObserver / img load / regionBubbles.changes 등 외부 트리거가 동시 다발로 들어오는 환경에서
    // 매번 0으로 되돌리면 mapBox 크기가 계속 0인 상황에서도 MAX_LAYOUT_RETRIES 한도가 사실상 무력화된다.
    // 리셋은 (1) 레이아웃이 실제로 성공한 경우(updateBubbleLayout 끝) 또는
    // (2) 한도를 모두 소진한 경우(scheduleLayoutRetry) — 이때는 layoutAbandoned 플래그도 함께 활성화 — 에만 수행한다.
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null;
      this.updateBubbleLayout();
    });
  }

  private updateBubbleLayout(): void {
    if (!this.mapBox?.nativeElement || !this.regionBubbles) {
      // ngAfterViewInit 이후라면 이 분기는 비정상이다. dialects setter가 ngAfterViewInit 전에 호출되는 경로 등에서 들어올 수 있으므로 진단을 남긴다.
      // 이 경로 자체의 1회만 경고(연속 호출 폭주 차단). ngAfterViewInit의 가드와는 별도 플래그라 한쪽이 다른 쪽을 침묵시키지 않는다.
      if (!this.warnedViewBindingMissingInUpdate) {
        this.warnedViewBindingMissingInUpdate = true;
        console.warn(
          'DialectMapComponent: ViewChild/ViewChildren 미바인딩 상태에서 updateBubbleLayout 호출됨',
          {
            wordId: this.word?.id,
            hasMapBox: !!this.mapBox?.nativeElement,
            hasRegionBubbles: !!this.regionBubbles,
          },
        );
      }
      this.setMapBottomPadding(0);
      return;
    }
    if (!this.listRegionBox?.length) {
      this.setMapBottomPadding(0);
      return;
    }

    this.regionBubbles.forEach(bubble => this.resetBubblePosition(bubble.nativeElement));

    const viewRect = this.mapBox.nativeElement.getBoundingClientRect();
    if (viewRect.width <= 0 || viewRect.height <= 0) {
      this.scheduleLayoutRetry();
      return;
    }

    const droppedBoxes: Array<{index: number; width: number; height: number}> = [];
    const boxes = this.regionBubbles
      .map((bubble, index) => {
        const box = this.createLayoutBox(bubble.nativeElement, index, viewRect);
        if (box === null) {
          const rect = bubble.nativeElement.getBoundingClientRect();
          droppedBoxes.push({index, width: Math.ceil(rect.width), height: Math.ceil(rect.height)});
        }
        return box;
      })
      .filter((box): box is BubbleLayoutElement => box !== null);

    if (droppedBoxes.length > 0) {
      // 폭/높이가 0인 말풍선은 레이아웃 대상에서 빠지므로 SCSS 기본 위치로 남고 다른 말풍선과 겹쳐 보일 수 있다.
      console.warn(
        'DialectMapComponent: 폭/높이가 0인 말풍선이 레이아웃 대상에서 제외됨(SCSS 기본 위치 유지)',
        {wordId: this.word?.id, droppedBoxes},
      );
    }

    const placements = resolveBubblePlacements(boxes, {
      width: viewRect.width,
      height: viewRect.height,
    });

    if (placements.length < boxes.length) {
      const placedIndices = new Set(placements.map(p => p.index));
      const missingBoxes = boxes
        .filter(box => !placedIndices.has(box.index))
        .map(box => ({
          index: box.index,
          baseX: box.baseX,
          baseY: box.baseY,
          width: box.width,
          height: box.height,
        }));
      console.warn(
        'DialectMapComponent: 배치 결과가 일부 누락됨(SCSS 기본 위치 유지)',
        {
          wordId: this.word?.id,
          boxCount: boxes.length,
          placedCount: placements.length,
          missingBoxes,
          viewSize: {width: viewRect.width, height: viewRect.height},
        },
      );
    }

    placements.forEach(placement => {
      const box = boxes.find(item => item.index === placement.index);
      if (box) {
        this.applyBubblePosition(box.el, placement);
      }
      else {
        console.warn(
          'DialectMapComponent: placement.index에 매칭되는 박스를 찾지 못함',
          {wordId: this.word?.id, placement, boxIndices: boxes.map(b => b.index)},
        );
      }
    });

    // 여기까지 도달하면 mapBox 크기가 정상이고 레이아웃을 한 번 수행했다는 뜻 — 재시도 카운터를 리셋한다.
    this.retryCount = 0;
    this.setMapBottomPadding(0);
  }

  private scheduleLayoutRetry(): void {
    // mapBox 크기가 0이면 레이아웃 직전 단계라 재시도. 다만 호출 주기를 한정해야 무한 루프를 막을 수 있다.
    if (this.retryCount >= DialectMapComponent.MAX_LAYOUT_RETRIES) {
      console.warn(
        `DialectMapComponent: mapBox 크기가 ${DialectMapComponent.MAX_LAYOUT_RETRIES}회 재시도 동안 0으로 유지되어 배치를 포기함`,
        {
          wordId: this.word?.id,
          regionBoxCount: this.listRegionBox?.length ?? 0,
          hasMapBox: !!this.mapBox?.nativeElement,
        },
      );
      // 후속 외부 트리거(ResizeObserver 등)가 동일한 사이클을 반복해 경고를 폭주시키는 것을 차단한다.
      // dialects 입력이 새로 들어오면 데이터가 바뀌었다는 의미이므로 setter에서 플래그/카운터를 리셋한다.
      this.layoutAbandoned = true;
      this.abandonedTriggerLogged = false;
      this.retryCount = 0;
      return;
    }
    if (this.retryHandle !== null) {
      clearTimeout(this.retryHandle);
    }
    this.retryCount += 1;
    this.retryHandle = setTimeout(() => {
      this.retryHandle = null;
      // scheduleBubbleLayout을 거치지 않고 updateBubbleLayout을 직접 호출한다.
      // 외부 트리거 경로(scheduleBubbleLayout)와 의미적으로 분리해 "재시도는 내부 흐름"임을 분명히 하고,
      // 이미 retryHandle/layoutFrame 정리가 setTimeout 콜백 시작 시점에 끝난 상태라 추가 정리 호출이 불필요하다.
      if (this.layoutFrame !== null) {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = null;
      }
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.updateBubbleLayout();
      });
    }, DialectMapComponent.LAYOUT_RETRY_DELAY_MS);
  }

  private resetBubblePosition(el: HTMLElement): void {
    el.style.left = '';
    el.style.top = '';
    el.style.right = '';
    el.style.bottom = '';
    el.style.marginLeft = '';
    el.style.marginTop = '';
  }

  private createLayoutBox(el: HTMLElement, index: number, viewRect: DOMRect): BubbleLayoutElement | null {
    const rect = el.getBoundingClientRect();
    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);

    if (width <= 0 || height <= 0) {
      return null;
    }

    return {
      index,
      el,
      width,
      height,
      baseX: rect.left - viewRect.left,
      baseY: rect.top - viewRect.top,
    };
  }

  private applyBubblePosition(el: HTMLElement, placement: BubblePlacement): void {
    el.style.left = `${placement.x}px`;
    el.style.top = `${placement.y}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }

  private setMapBottomPadding(bottomPadding: number): void {
    if (this.mapBottomPadding === bottomPadding) {
      return;
    }

    this.mapBottomPadding = bottomPadding;
    this.cdr.markForCheck();
  }

  protected readonly SimpleWord = SimpleWord;
}
